import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDraw } from "./MapDrawController";
import CustomDrawButtons from "./CustomDrawButtons";
import ForestCoverAnalysis from "./ForestCoverAnalysis";

// Financial metrics calculation function (unchanged)
const calculateFinancialMetrics = (annualCreditsCO2, polygonArea) => {
  const landSize = polygonArea;
  const timeline = 30;
  const discountRate = 0.10;
  const establishmentCost = 25;
  const maintenanceCost = 10;
  const startingCarbonPrice = 5.80;
  const growthRateAfterYear5 = 0.05;

  const yearlyData = [];
  let npv = 0;

  for (let year = 0; year <= timeline; year++) {
    const carbonPrice = year <= 5
      ? startingCarbonPrice
      : startingCarbonPrice * Math.pow(1 + growthRateAfterYear5, year - 5);
    const creditsSold = annualCreditsCO2;
    const establishment = year === 0 ? landSize * establishmentCost : 0;
    const maintenance = year >= 1 ? landSize * maintenanceCost : 0;
    const revenue = carbonPrice * creditsSold;
    const netFlow = revenue - establishment - maintenance;
    const discountFactor = 1 / Math.pow(1 + discountRate, year);
    const discountedNetFlow = netFlow * discountFactor;

    npv += discountedNetFlow;

    yearlyData.push({
      year,
      carbonPrice,
      creditsSold,
      establishment,
      maintenance,
      revenue,
      netFlow,
      discountFactor,
      discountedNetFlow,
    });
  }

  let irr = 0; // Placeholder for IRR
  let cashFlows = yearlyData.map((year) => year.netFlow);

  return {
    npv,
    irr,
    yearlyData,
    totalRevenue: yearlyData.reduce((sum, year) => sum + year.revenue, 0),
    totalCosts: yearlyData.reduce((sum, year) => sum + year.establishment + year.maintenance, 0),
  };
};

const CarbonAnalysisPanel = ({ map, onClose, onAnalyzePolygon }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState({
    greenZone: null,
    forestCover: null,
    formulaD: null,
    bgb: null,
    soc: null,
    eligibleCO2: null,
    finalEligibleCO2: null,
  });
  const [financialResults, setFinancialResults] = useState(null);
  const [calculatedAnnualCredits, setCalculatedAnnualCredits] = useState(null);

  const { enterDrawingMode, hideDrawToolbar, polygonArea, deletePolygon } = useDraw();
  console.log("useDraw output:", { enterDrawingMode, hideDrawToolbar, polygonArea, deletePolygon });

  const { analyzeForestCover } = ForestCoverAnalysis({ map });

  const discountRate = 0.1;
  const estCost = 25.0;
  const maintenanceCost = 10.0;
  const carbonPrice = 5.8;
  const growthRate = 0.05;
  const timeline = 30;

const [annualCredits, setAnnualCredits] = useState(null);

  const greenZoneClasses = [
    6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22,
    28, 29, 30,
  ];

  const getDrawInstance = () => {
    if (window.labelsMapInstance && window.labelsMapInstance.__draw) {
      return window.labelsMapInstance.__draw;
    }
    if (window.layersMapInstance && window.layersMapInstance.__draw) {
      return window.layersMapInstance.__draw;
    }
    if (map && map.__draw) {
      return map.__draw;
    }
    return null;
  };

  const showAnalysisBox = () => {
    console.log("Creating comprehensive analysis box");
    const existingBox = document.getElementById("comprehensive-analysis-box");
    if (existingBox) existingBox.remove();

    const forestBox = document.getElementById("forest-cover-analysis-box");
    if (forestBox) forestBox.remove();

    const greenZoneBox = document.getElementById("direct-analysis-box");
    if (greenZoneBox) greenZoneBox.remove();

    const box = document.createElement("div");
    box.id = "comprehensive-analysis-box";

    box.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:15px;">
        <h3 style="margin:0;font-size:18px;font-weight:700;color:#2e3a59;">Comprehensive Land Analysis</h3>
        <button id="close-analysis" style="background:none;border:none;cursor:pointer;font-size:18px;font-weight:bold;">×</button>
      </div>
      <div id="analysis-content" style="padding:20px;text-align:center;">
        <div style="margin-bottom:15px;">Analysis in progress...</div>
        <div id="analysis-spinner" style="width:30px;height:30px;border:4px solid #f3f3f3;border-top:4px solid #3498db;border-radius:50%;margin:0 auto;"></div>
      </div>
    `;

    Object.assign(box.style, {
      position: "fixed",
      bottom: "80px",
      left: "50%",
      transform: "translateX(-50%)",
      width: "400px",
      padding: "20px",
      background: "white",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15), 0 0 0 2px #2e3a59",
      zIndex: "99999",
      fontFamily: "'Figtree', sans-serif, Arial",
      borderRadius: "12px",
      color: "#2e3a59",
      maxHeight: "80vh",
      overflowY: "auto",
    });

    const style = document.createElement("style");
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      #analysis-spinner {
        animation: spin 1s linear infinite;
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(box);

    document.getElementById("close-analysis").addEventListener("click", () => {
      box.remove();
      removeAllVisualizationsFromMap();
    });

    return box;
  };

  const updateAnalysisBoxWithError = (errorMessage) => {
    const contentDiv = document.getElementById("analysis-content");
    if (!contentDiv) return;
    const spinner = document.getElementById("analysis-spinner");
    if (spinner) spinner.remove();
    contentDiv.innerHTML = `
      <div style="padding:10px;background-color:#ffebee;color:#d32f2f;border-radius:6px;margin-bottom:10px;">
        ${errorMessage || "An error occurred during analysis."}
      </div>
    `;
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  };

  const updateAnalysisBoxWithResults = (
    greenZoneData,
    forestCoverData,
    formulaDData = null,
    bgbData = null,
    socData = null
  ) => {
    const contentDiv = document.getElementById("analysis-content");
    if (!contentDiv) return;

    const spinner = document.getElementById("analysis-spinner");
    if (spinner) spinner.remove();

    const greenPercentage = greenZoneData ? greenZoneData.greenZonePercentage.toFixed(2) : "N/A";
    const forestPercentage = forestCoverData ? forestCoverData.forestCoverPercentage.toFixed(2) : "N/A";
    const greenArea = greenZoneData ? greenZoneData.greenArea.toFixed(2) : "N/A";
    const forestArea = forestCoverData ? forestCoverData.forestArea.toFixed(2) : "N/A";

    const formulaE =
      greenZoneData && forestCoverData
        ? ((greenZoneData.greenZonePercentage / 100) * (forestCoverData.forestCoverPercentage / 100) * 100).toFixed(2)
        : "N/A";

    let formulaDValue = formulaDData ? formulaDData.formulaD.toFixed(2) : "N/A";
    let isNegativeD = false;

    if (formulaDData && typeof formulaDData.formulaD === "number") {
      if (formulaDData.formulaD < 0) {
        isNegativeD = true;
        formulaDValue = `${formulaDData.formulaD.toFixed(2)} (capped at 0% for calculations)`;
      }
    }

    const formulaFYears = 30;
    let formulaFValue = "N/A";
    let formulaFForCalculations = 0;

    if (formulaDData && typeof formulaDData.formulaD === "number") {
      const d = isNegativeD ? 0 : formulaDData.formulaD / 100;
      const fractionThreatened = 1 - Math.pow(1 - d, formulaFYears);
      formulaFValue = (fractionThreatened * 100).toFixed(2);
      formulaFForCalculations = fractionThreatened;

      if (isNegativeD) {
        formulaFValue += " (adjusted from negative value)";
      }
    }

    const agbValue = bgbData && typeof bgbData.agbPerHa === 'number' ? bgbData.agbPerHa.toFixed(2) : "N/A";
    const bgbValue = bgbData && typeof bgbData.bgbPerHa === 'number' ? bgbData.bgbPerHa.toFixed(2) : "N/A";
    const bgbAgbRatio = bgbData && typeof bgbData.bgbAgbRatio === 'number' ? bgbData.bgbAgbRatio.toFixed(2) : "N/A";

    const totalBiomassPerHa = bgbData && typeof bgbData.agbPerHa === 'number' && typeof bgbData.bgbPerHa === 'number'
      ? (bgbData.agbPerHa + bgbData.bgbPerHa).toFixed(2)
      : "N/A";

    const totalBiomass = bgbData && typeof bgbData.agbTotal === 'number' && typeof bgbData.bgbTotal === 'number'
      ? ((bgbData.agbTotal + bgbData.bgbTotal) / 1000000).toFixed(2)
      : "N/A";

    const bgcValue = bgbData && typeof bgbData.bgbPerHa === 'number'
      ? (bgbData.bgbPerHa * 0.475 * 3.67).toFixed(2)
      : "N/A";

    const bgcTotal = bgbData && typeof bgbData.bgbTotal === 'number'
      ? ((bgbData.bgbTotal * 0.475 * 3.67) / 1000000).toFixed(2)
      : "N/A";

    const bgc2Value = bgcValue !== "N/A"
      ? (parseFloat(bgcValue) * 0.744).toFixed(2)
      : "N/A";

    const bgc2Total = bgcTotal !== "N/A"
      ? (parseFloat(bgcTotal) * 0.744).toFixed(2)
      : "N/A";

    let soc1Value = "N/A",
        soc2Value = "N/A",
        socCO2eValue = "N/A",
        socCO2eTotal = "N/A",
        socCombinedValue = "N/A";

    if (socData) {
      soc1Value = socData.soc1PerHa !== undefined ? parseFloat(socData.soc1PerHa).toFixed(2) : "0";
      soc2Value = socData.soc2PerHa !== undefined ? parseFloat(socData.soc2PerHa).toFixed(2) : "0";
      socCO2eValue = socData.socCO2ePerHa !== undefined ? parseFloat(socData.socCO2ePerHa).toFixed(2) : "N/A";
      socCO2eTotal = socData.socCO2eTotalMillionTons !== undefined ? parseFloat(socData.socCO2eTotalMillionTons).toFixed(2) : "N/A";
      socCombinedValue = socData.socPerHa !== undefined ? parseFloat(socData.socPerHa).toFixed(2) : "N/A";
    }

    const agcValue = bgbData && typeof bgbData.agbPerHa === 'number'
      ? (bgbData.agbPerHa * 0.475 * 3.67).toFixed(2)
      : "N/A";

    const agcTotal = bgbData && typeof bgbData.agbTotal === 'number'
      ? ((bgbData.agbTotal * 0.475 * 3.67) / 1000000).toFixed(2)
      : "N/A";

    let eligibleCO2 = "N/A";
    let finalEligibleCO2 = "N/A";
    let annualCreditsCO2 = "N/A";
    let metrics = null;
    let totalAnnualCredits = 0;

 if (bgbData && formulaDData && forestCoverData && greenZoneData &&
    typeof bgbData.agbPerHa === 'number' && typeof bgbData.bgbPerHa === 'number') {
  const agc1 = bgbData.agbPerHa * 0.475 * 3.67;
  const bgc2 = bgbData.bgbPerHa * 0.475 * 3.67 * 0.744;
  const socValue = socData && socData.socCO2ePerHa ? socData.socCO2ePerHa : 0;

  const f_t = formulaFForCalculations;
  const e = (greenZoneData.greenZonePercentage / 100) * (forestCoverData.forestCoverPercentage / 100);

  eligibleCO2 = ((agc1 + bgc2 + socValue) * f_t * e).toFixed(2);
  finalEligibleCO2 = (parseFloat(eligibleCO2) * 0.80).toFixed(2);
  annualCreditsCO2 = (parseFloat(finalEligibleCO2) / formulaFYears).toFixed(2);

  if (isNegativeD) {
    const warningDiv = document.createElement("div");
    warningDiv.style.padding = "10px";
    warningDiv.style.backgroundColor = "#fff3cd";
    warningDiv.style.color = "#856404";
    warningDiv.style.borderRadius = "6px";
    warningDiv.style.marginTop = "15px";
    warningDiv.style.marginBottom = "15px";
    warningDiv.innerHTML = `
      <strong>Note:</strong> A negative value was detected in Formula D (${formulaDData.formulaD.toFixed(2)}%),
      indicating more fire loss than total tree cover loss. This has been adjusted to 0% for carbon
      calculations, as negative deforestation would mean forest growth.
    `;
    contentDiv.appendChild(warningDiv);
  }

  setAnalysisResults((prev) => ({
    ...prev,
    eligibleCO2,
    finalEligibleCO2,
  }));

  const annualCreditsCO2Value = parseFloat(finalEligibleCO2) / formulaFYears;
  totalAnnualCredits = annualCreditsCO2Value * greenZoneData.totalArea;
  setCalculatedAnnualCredits(totalAnnualCredits);
}

// 2. Replace with this (changes marked with // CHANGED comments)
if (bgbData && formulaDData && forestCoverData && greenZoneData &&
    typeof bgbData.agbPerHa === 'number' && typeof bgbData.bgbPerHa === 'number') {
  const agc1 = bgbData.agbPerHa * 0.475 * 3.67;
  const bgc2 = bgbData.bgbPerHa * 0.475 * 3.67 * 0.744;
  const socValue = socData && socData.socCO2ePerHa ? socData.socCO2ePerHa : 0;

  const f_t = formulaFForCalculations;
  const e = (greenZoneData.greenZonePercentage / 100) * (forestCoverData.forestCoverPercentage / 100);

  // CHANGED: Calculate exact values without premature rounding
  const eligibleCO2Exact = (agc1 + bgc2 + socValue) * f_t * e;
  const finalEligibleCO2Exact = eligibleCO2Exact * 0.80;
  const annualCreditsCO2Exact = finalEligibleCO2Exact / formulaFYears;

  // CHANGED: Format for display
  eligibleCO2 = eligibleCO2Exact.toFixed(2);
  finalEligibleCO2 = finalEligibleCO2Exact.toFixed(2);
  annualCreditsCO2 = annualCreditsCO2Exact.toFixed(2);

  // CHANGED: Calculate total credits using the exact value
  totalAnnualCredits = annualCreditsCO2Exact * greenZoneData.totalArea;

  if (isNegativeD) {
    const warningDiv = document.createElement("div");
    warningDiv.style.padding = "10px";
    warningDiv.style.backgroundColor = "#fff3cd";
    warningDiv.style.color = "#856404";
    warningDiv.style.borderRadius = "6px";
    warningDiv.style.marginTop = "15px";
    warningDiv.style.marginBottom = "15px";
    warningDiv.innerHTML = `
      <strong>Note:</strong> A negative value was detected in Formula D (${formulaDData.formulaD.toFixed(2)}%),
      indicating more fire loss than total tree cover loss. This has been adjusted to 0% for carbon
      calculations, as negative deforestation would mean forest growth.
    `;
    contentDiv.appendChild(warningDiv);
  }

  setAnalysisResults((prev) => ({
    ...prev,
    eligibleCO2,
    finalEligibleCO2,
  }));

  // CHANGED: The calculation is now done above, just set the result here
  setCalculatedAnnualCredits(totalAnnualCredits);
}

    if (totalAnnualCredits > 0 && greenZoneData && greenZoneData.totalArea) {
      metrics = calculateFinancialMetrics(totalAnnualCredits, greenZoneData.totalArea);
      setFinancialResults(metrics);
    }

    const formattedYearlyData = metrics && metrics.yearlyData
      ? metrics.yearlyData.slice(0, 6).map((year) => ({
          year: year.year,
          carbonPrice: '$' + year.carbonPrice.toFixed(2),
          revenue: '$' + year.revenue.toLocaleString(undefined, { maximumFractionDigits: 0 }),
          costs: '$' + (year.establishment + year.maintenance).toLocaleString(undefined, { maximumFractionDigits: 0 }),
          netFlow: '$' + year.netFlow.toLocaleString(undefined, { maximumFractionDigits: 0 }),
          discountedNetFlow: '$' + year.discountedNetFlow.toLocaleString(undefined, { maximumFractionDigits: 0 }),
          isNetFlowPositive: year.netFlow >= 0,
        }))
      : [];

    const formattedTotalRevenue = metrics ? formatCurrency(metrics.totalRevenue) : '$0';
    const formattedTotalCosts = metrics ? formatCurrency(metrics.totalCosts) : '$0';
    const formattedNPV = metrics ? formatCurrency(metrics.npv) : '$0';
    const formattedCarbonPrice = '$' + carbonPrice.toFixed(2);

    contentDiv.innerHTML = `
      <div style="width:100%;">
        <div style="margin-bottom:15px;">
          <div style="font-weight:600;margin-bottom:5px;">Selected Area (ha):</div>
          <div style="padding:10px;background:#f7f9fc;border-radius:6px;border:1px solid #e1e5eb;">
            ${
              greenZoneData
                ? greenZoneData.totalArea.toFixed(2)
                : forestCoverData
                ? forestCoverData.totalArea.toFixed(2)
                : "N/A"
            }
          </div>
        </div>

        <div style="display:flex;gap:15px;margin-bottom:15px;">
          <div style="flex:1;border:1px solid #e1e5eb;border-radius:6px;padding:15px;background:#f7f9fc;">
            <div style="text-align:center;font-weight:600;margin-bottom:10px;color:#2e3a59;">Formula A</div>
            <div style="font-size:24px;text-align:center;font-weight:700;color:#2e3a59;">${greenPercentage}%</div>
            <div style="text-align:center;font-size:12px;margin-bottom:10px;color:#666;">Green Zone Area: ${greenArea} ha</div>
            <div style="text-align:center;font-size:12px;color:#666;">A = (Green Zone Area / Total Area) * 100</div>
          </div>

          <div style="flex:1;border:1px solid #e1e5eb;border-radius:6px;padding:15px;background:#f7f9fc;">
            <div style="text-align:center;font-weight:600;margin-bottom:10px;color:#2e3a59;">Formula B</div>
            <div style="font-size:24px;text-align:center;font-weight:700;color:#2e3a59;">${forestPercentage}%</div>
            <div style="text-align:center;font-size:12px;margin-bottom:10px;color:#666;">Forest Cover Area: ${forestArea} ha</div>
            <div style="text-align:center;font-size:12px;color:#666;">B = (Forest Cover ≥30% ≥5m / Total Area) * 100</div>
          </div>
        </div>

        <div style="margin-bottom:15px;border:1px solid #e1e5eb;border-radius:6px;padding:15px;background:#f5f5f5;">
          <div style="text-align:center;font-weight:600;margin-bottom:10px;color:#2e3a59;">Formula D</div>
          <div style="font-size:28px;text-align:center;font-weight:700;color:#2e3a59;">
            ${formulaDValue}%
          </div>
          <div style="text-align:center;font-size:12px;color:#666;margin-top:8px;">
            D = (Tree Cover Loss - Tree Cover Loss Due To Fire) / Forest Cover * 100
          </div>
          ${
            formulaDData
              ? `
          <div style="display:flex;justify-content:space-between;margin-top:10px;font-size:12px;color:#666;">
            <div>Tree Cover Loss: ${formulaDData.treeCoverLossArea.toFixed(2)} ha</div>
            <div>Fire Loss: ${formulaDData.fireLossArea.toFixed(2)} ha</div>
          </div>
          <div style="display:flex;justify-content:space-between;margin-top:5px;font-size:12px;color:#666;">
            <div>Non-Fire Loss: ${formulaDData.nonFireLossArea.toFixed(2)} ha</div>
            <div>Forest Cover: ${formulaDData.forestArea.toFixed(2)} ha</div>
          </div>
          `
              : ""
          }
        </div>

        <div style="margin-bottom:15px;border:1px solid #fff0f3;border-radius:6px;padding:15px;background:#fff5f7;">
          <div style="text-align:center;font-weight:600;margin-bottom:10px;color:#c2185b;">Formula F</div>
          <div style="font-size:28px;text-align:center;font-weight:700;color:#c2185b;">
            ${formulaFValue}%
          </div>
          <div style="text-align:center;font-size:12px;color:#666;margin-top:8px;">
            F = 1 - (1 - D/100)^30
          </div>
          <div style="text-align:center;font-size:12px;color:#666;margin-top:8px;">
            Fraction of forest threatened after 30 years
          </div>
        </div>

        <div style="margin-bottom:15px;border:1px solid #d1c4e9;border-radius:6px;padding:15px;background:#ede7f6;">
          <div style="text-align:center;font-weight:600;margin-bottom:10px;color:#4527a0;">Formula E</div>
          <div style="font-size:28px;text-align:center;font-weight:700;color:#4527a0;">
            ${formulaE}%
          </div>
          <div style="text-align:center;font-size:12px;color:#666;margin-top:8px;">E = A * B</div>
        </div>

        <div style="margin-bottom:15px;border:1px solid #e3f2fd;border-radius:6px;padding:15px;background:#e8f5e9;">
          <div style="text-align:center;font-weight:600;margin-bottom:10px;color:#1b5e20;">Biomass Calculation</div>
          ${
            bgbData
              ? `
          <div style="display:flex;gap:15px;margin-top:15px;">
            <div style="flex:1;text-align:center;padding:10px;background:#f1f8e9;border-radius:6px;">
              <div style="font-weight:600;margin-bottom:5px;color:#33691e;">AGB (t/ha)</div>
              <div style="font-size:18px;font-weight:700;color:#33691e;">${agbValue}</div>
            </div>
            <div style="flex:1;text-align:center;padding:10px;background:#f1f8e9;border-radius:6px;">
              <div style="font-weight:600;margin-bottom:5px;color:#33691e;">BGB (t/ha)</div>
              <div style="font-size:18px;font-weight:700;color:#33691e;">${bgbValue}</div>
            </div>
            <div style="flex:1;text-align:center;padding:10px;background:#f1f8e9;border-radius:6px;">
              <div style="font-weight:600;margin-bottom:5px;color:#33691e;">BGB:AGB Ratio</div>
              <div style="font-size:18px;font-weight:700;color:#33691e;">${bgbAgbRatio}</div>
            </div>
          </div>

          <div style="margin-top:15px;padding:10px;background:#dcedc8;border-radius:6px;">
            <div style="text-align:center;font-weight:600;margin-bottom:8px;color:#33691e;">BGC1 Formula</div>
            <div style="text-align:center;font-size:16px;font-weight:600;color:#33691e;">
              BGC1 = (BGB * 0.475) * 3.67
            </div>
            <div style="margin-top:8px;display:flex;justify-content:space-between;">
              <div style="font-weight:600;">Result:</div>
              <div style="font-weight:700;">${bgcValue} tCO₂e/ha</div>
            </div>
            <div style="margin-top:4px;display:flex;justify-content:space-between;font-size:12px;color:#555;">
              <div>(${bgbValue} t/ha * 0.475 * 3.67)</div>
              <div>Total: ${bgcTotal} million tCO₂e</div>
            </div>
          </div>

          <div style="margin-top:15px;padding:10px;background:#c8e6c9;border-radius:6px;">
            <div style="text-align:center;font-weight:600;margin-bottom:8px;color:#33691e;">BGC2 Formula (10-year decay)</div>
            <div style="text-align:center;font-size:16px;font-weight:600;color:#33691e;">
              BGC2 = BGC1 * 0.744
            </div>
            <div style="margin-top:8px;display:flex;justify-content:space-between;">
              <div style="font-weight:600;">Result:</div>
              <div style="font-weight:700;">${bgc2Value} tCO₂e/ha</div>
            </div>
            <div style="margin-top:4px;display:flex;justify-content:space-between;font-size:12px;color:#555;">
              <div>(${bgcValue} * 0.744)</div>
              <div>Total: ${bgc2Total} million tCO₂e</div>
            </div>
          </div>

          <div style="margin-top:15px;text-align:center;font-weight:600;color:#1b5e20;">
            Total Biomass: ${totalBiomassPerHa} t/ha (${totalBiomass} million tons)
          </div>
          `
              : `
          <div style="text-align:center;padding:15px;color:#666;font-style:italic;">
            Biomass data not available for this area
          </div>
          `
          }
        </div>

        ${
          bgbData && typeof bgbData.agbPerHa === 'number'
            ? `
        <div style="margin-top:15px; margin-bottom: 15px;padding:10px;background:#dcedc8;border-radius:6px;">
          <div style="text-align:center;font-weight:600;margin-bottom:8px;color:#33691e;">AGC1 Formula</div>
          <div style="text-align:center;font-size:16px;font-weight:600;color:#33691e;">
            AGC1 = (AGB * 0.475) * 3.67
          </div>
          <div style="margin-top:8px;display:flex;justify-content:space-between;">
            <div style="font-weight:600;">Result:</div>
            <div style="font-weight:700;">${agcValue} tCO₂e/ha</div>
          </div>
          <div style="margin-top:4px;display:flex;justify-content:space-between;font-size:12px;color:#555;">
            <div>(${agbValue} t/ha * 0.475 * 3.67)</div>
            <div>Total: ${agcTotal} million tCO₂e</div>
          </div>
        </div>
        `
            : ''
        }

        <div style="margin-bottom:15px;border:1px solid #e3f2fd;border-radius:6px;padding:15px;background:#e1f5fe;">
          <div style="text-align:center;font-weight:600;margin-bottom:10px;color:#01579b;">Soil Organic Carbon</div>
          ${
            socData
              ? `
          <div style="display:flex;gap:15px;margin-top:15px;">
            <div style="flex:1;text-align:center;padding:10px;background:#e1f5fe;border-radius:6px;">
              <div style="font-weight:600;margin-bottom:5px;color:#0277bd;">SOC1 (t/ha)</div>
              <div style="font-size:18px;font-weight:700;color:#0277bd;">
                ${
                  socData.hasSoc1Data
                    ? soc1Value
                    : "<span style='color:#b71c1c;font-size:14px;'>No data</span>"
                }
              </div>
            </div>
            <div style="flex:1;text-align:center;padding:10px;background:#e1f5fe;border-radius:6px;">
              <div style="font-weight:600;margin-bottom:5px;color:#0277bd;">SOC2 (t/ha)</div>
              <div style="font-size:18px;font-weight:700;color:#0277bd;">
                ${
                  socData.hasSoc2Data
                    ? soc2Value
                    : "<span style='color:#b71c1c;font-size:14px;'>No data</span>"
                }
              </div>
            </div>
          </div>

          <div style="margin-top:15px;padding:10px;background:#b3e5fc;border-radius:6px;">
            <div style="text-align:center;font-weight:600;margin-bottom:8px;color:#01579b;">SOC Formula</div>
            <div style="text-align:center;font-size:16px;font-weight:600;color:#01579b;">
              SOC = (SOC1 + SOC2) * 3.67
            </div>
            ${
              socData.validAreaPercentage > 0
                ? `
              <div style="margin-top:8px;display:flex;justify-content:space-between;">
                <div style="font-weight:600;">Result:</div>
                <div style="font-weight:700;">${socCO2eValue} tCO₂e/ha</div>
              </div>
              <div style="margin-top:4px;display:flex;justify-content:space-between;font-size:12px;color:#555;">
                <div>
                  ${
                    socData.hasSoc1Data && socData.hasSoc2Data
                      ? `(${soc1Value} + ${soc2Value}) * 3.67`
                      : socData.hasSoc1Data
                      ? `(${soc1Value} + 0) * 3.67`
                      : `(0 + ${soc2Value}) * 3.67`
                  }
                </div>
                <div>Total: ${socCO2eTotal} million tCO₂e</div>
              </div>
            `
                : `
              <div style="text-align:center;color:#b71c1c;padding:10px;">
                No valid data available for calculation
              </div>
            `
            }
          </div>

          <div style="margin-top:10px;font-size:12px;color:#555;text-align:center;">
            ${
              socData.validAreaPercentage > 0
                ? `Valid data area: ${socData.validAreaPercentage.toFixed(2)}% of total area`
                : 'No valid SOC data found in the selected area'
            }
          </div>

          ${
            socData.meta && socData.meta.note
              ? `
            <div style="margin-top:8px;font-size:12px;font-style:italic;color:#555;text-align:center;">
              Note: ${socData.meta.note}
            </div>
          `
              : ""
          }

          ${
            socData.validAreaPercentage > 0
              ? `
            <div style="margin-top:15px;text-align:center;font-weight:600;color:#01579b;">
              Combined SOC: ${socCombinedValue} t/ha
            </div>
          `
              : ""
          }
          `
              : `
          <div style="text-align:center;padding:15px;color:#666;font-style:italic;">
            Soil carbon data not available for this area
          </div>
          `
          }
        </div>

        <div style="margin-top:20px;padding:15px;background:#ede7f6;border-radius:6px;">
          <div style="text-align:center;font-weight:600;margin-bottom:15px;color:#4527a0;font-size:18px;">
            Carbon Credit Eligibility
          </div>

          <div style="padding:12px;background:#d1c4e9;border-radius:6px;margin-bottom:15px;">
            <div style="text-align:center;font-weight:600;margin-bottom:8px;color:#4527a0;">
              Eligible CO₂
            </div>
            <div style="text-align:center;font-size:16px;font-weight:600;color:#4527a0;margin-bottom:10px;">
              Eligible CO₂ = (AGC1 + BGC2 + SOC) * f_t * e
            </div>
            <div style="font-size:14px;color:#4527a0;margin-bottom:5px;">Where:</div>
            <div style="font-size:12px;color:#555;margin-left:15px;margin-bottom:3px;">
              • f_t is Formula F (fraction threatened): ${formulaFValue}%
            </div>
            <div style="font-size:12px;color:#555;margin-left:15px;margin-bottom:10px;">
              • e is Formula E (green zone * forest cover): ${formulaE}%
            </div>
            <div style="margin-top:10px;display:flex;justify-content:space-between;align-items:center;">
              <div style="font-weight:600;">Result:</div>
              <div style="font-size:22px;font-weight:700;color:#4527a0;">${eligibleCO2} tCO₂e/ha</div>
            </div>
          </div>

          <div style="padding:12px;background:#b39ddb;border-radius:6px;">
            <div style="text-align:center;font-weight:600;margin-bottom:8px;color:#4527a0;">
              Final Eligible CO₂ (with 20% buffer)
            </div>
            <div style="text-align:center;font-size:16px;font-weight:600;color:#4527a0;margin-bottom:10px;">
              Final Eligible CO₂ = Eligible CO₂ * 0.80
            </div>
            <div style="margin-top:10px;display:flex;justify-content:space-between;align-items:center;">
              <div style="font-weight:600;">Result:</div>
              <div style="font-size:24px;font-weight:700;color:#4527a0;">${finalEligibleCO2} tCO₂e/ha</div>
            </div>
          </div>

          <div style="padding:12px;background:#9575cd;border-radius:6px;margin-top:15px;">
            <div style="text-align:center;font-weight:600;margin-bottom:8px;color:#fff;">
              Annual Credits CO₂
            </div>
            <div style="text-align:center;font-size:16px;font-weight:600;color:#fff;margin-bottom:10px;">
              Annual Credits CO₂ = Final Eligible CO₂ / ${formulaFYears} years
            </div>
            <div style="margin-top:10px;display:flex;justify-content:space-between;align-items:center;">
              <div style="font-weight:600;color:#fff;">Result:</div>
              <div style="font-size:24px;font-weight:700;color:#fff;">${annualCreditsCO2} tCO₂e/ha/year</div>
            </div>
          </div>

          ${
            greenZoneData && finalEligibleCO2 !== "N/A"
              ? `
              <div style="margin-top:15px;text-align:center;font-weight:700;color:#4527a0;font-size:16px;">
                Total Final Eligible CO₂: ${(parseFloat(finalEligibleCO2) * greenZoneData.totalArea).toFixed(2)} tCO₂e
              </div>
              <div style="margin-top:5px;text-align:center;font-weight:700;color:#4527a0;font-size:16px;">
Total Annual Credits: ${totalAnnualCredits.toFixed(2)} tCO₂e/year              </div>
              `
              : ""
          }
        </div>

        ${
          metrics && finalEligibleCO2 !== "N/A" && greenZoneData
            ? `
            <div style="margin-top:20px;padding:15px;background:#e8f5e9;border-radius:6px;">
              <div style="text-align:center;font-weight:600;margin-bottom:15px;color:#2e7d32;font-size:18px;">
                30-Year Financial Summary
              </div>

              <div style="display:flex;gap:5px;margin-bottom:15px;">
                <div style="flex:1;border:1px solid #c8e6c9;border-radius:6px;padding:15px;background:#f1f8e9;">
                  <div style="text-align:center;font-weight:600;margin-bottom:10px;color:#2e7d32;">Total Revenue</div>
                  <div style="font-size:18px;text-align:center;font-weight:700;color:#2e7d32;">${formattedTotalRevenue}</div>
                </div>

                <div style="flex:1;border:1px solid #c8e6c9;border-radius:6px;padding:15px;background:#f1f8e9;">
                  <div style="text-align:center;font-weight:600;margin-bottom:10px;color:#2e7d32;">Total Costs</div>
                  <div style="font-size:18px;text-align:center;font-weight:700;color:#2e7d32;">${formattedTotalCosts}</div>
                </div>
              </div>

              <div style="border:1px solid #81c784;border-radius:6px;padding:15px;background:#c8e6c9;margin-bottom:15px;">
                <div style="text-align:center;font-weight:600;margin-bottom:10px;color:#2e7d32;">Net Present Value (NPV)</div>
                <div style="font-size:24px;text-align:center;font-weight:700;color:#2e7d32;">${formattedNPV}</div>
                <div style="text-align:center;font-size:12px;color:#558b2f;margin-top:5px;">
                  Discount Rate: ${(discountRate * 100).toFixed(0)}%
                </div>
              </div>

              <div style="font-size:12px;color:#558b2f;text-align:center;margin-top:10px;">
                Based on ${totalAnnualCredits.toLocaleString(undefined, { maximumFractionDigits: 0 })} annual credits at ${carbonPrice.toFixed(2)}/tCO₂e initial price, growing ${(growthRate * 100).toFixed(0)}% annually after year 5
              </div>
            </div>

            <div style="margin-top:20px;padding:15px;background:#f5f5f5;border-radius:6px;">
              <div style="text-align:center;font-weight:600;margin-bottom:15px;color:#424242;font-size:16px;">
                Cash Flow Preview (First 5 Years)
              </div>

              <div style="overflow-x:auto;">
                <table style="width:100%;border-collapse:collapse;font-size:12px;">
                  <thead>
                    <tr style="background:#e0e0e0;">
                      <th style="padding:8px;border:1px solid #bdbdbd;text-align:center;">Year</th>
                      <th style="padding:8px;border:1px solid #bdbdbd;text-align:center;">Carbon Price</th>
                      <th style="padding:8px;border:1px solid #bdbdbd;text-align:center;">Revenue</th>
                      <th style="padding:8px;border:1px solid #bdbdbd;text-align:center;">Costs</th>
                      <th style="padding:8px;border:1px solid #bdbdbd;text-align:center;">Net Flow</th>
                      <th style="padding:8px;border:1px solid #bdbdbd;text-align:center;">Discounted</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${formattedYearlyData.map((year) => `
                    <tr>
                      <td style="padding:8px;border:1px solid #bdbdbd;text-align:center;">${year.year}</td>
                      <td style="padding:8px;border:1px solid #bdbdbd;text-align:right;">${year.carbonPrice}</td>
                      <td style="padding:8px;border:1px solid #bdbdbd;text-align:right;">${year.revenue}</td>
                      <td style="padding:8px;border:1px solid #bdbdbd;text-align:right;">${year.costs}</td>
                      <td style="padding:8px;border:1px solid #bdbdbd;text-align:right;font-weight:${year.isNetFlowPositive ? '600' : '400'};color:${year.isNetFlowPositive ? '#2e7d32' : '#c62828'};">${year.netFlow}</td>
                      <td style="padding:8px;border:1px solid #bdbdbd;text-align:right;">${year.discountedNetFlow}</td>
                    </tr>
                  `).join('')}
                  </tbody>
                  <tfoot>
                    <tr style="background:#e0e0e0;">
                      <td colspan="4" style="padding:8px;border:1px solid #bdbdbd;text-align:right;font-weight:600;">30-Year NPV:</td>
                      <td colspan="2" style="padding:8px;border:1px solid #bdbdbd;text-align:right;font-weight:700;color:#2e7d32;">${metrics ? '$' + metrics.npv.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '$0'}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            `
            : ""
        }

        <div style="margin-bottom:15px;">
          <div style="font-weight:600;margin-bottom:5px;">Analysis Summary:</div>
          <div style="display:flex;gap:10px;">
            <div style="flex:1;padding:10px;background:#f7f9fc;border-radius:6px;border:1px solid ${
              greenZoneData?.greenZonePercentage > 50 ? "#b7dfbd" : "#ffe0b2"
            };">
              <div style="font-weight:600;">Green Zone</div>
              <div>${
                greenZoneData?.greenZonePercentage > 75
                  ? "Excellent green coverage!"
                  : greenZoneData?.greenZonePercentage > 50
                  ? "Good green coverage."
                  : greenZoneData?.greenZonePercentage > 25
                  ? "Limited green coverage."
                  : "Minimal green coverage."
              }</div>
            </div>
            <div style="flex:1;padding:10px;background:#f7f9fc;border-radius:6px;border:1px solid ${
              forestCoverData?.forestCoverPercentage > 50 ? "#1b5e20" : "#e65100"
            };">
              <div style="font-weight:600;">Forest Cover</div>
              <div>${
                forestCoverData?.forestCoverPercentage > 75
                  ? "Excellent forest coverage!"
                  : forestCoverData?.forestCoverPercentage > 50
                  ? "Good forest coverage."
                  : forestCoverData?.forestCoverPercentage > 25
                  ? "Limited forest coverage."
                  : "Minimal forest coverage."
              }</div>
            </div>
          </div>
        </div>
      </div>
    `;

    const creditDiv = document.createElement("div");
    creditDiv.style.marginTop = "15px";
    creditDiv.style.fontSize = "12px";
    creditDiv.style.color = "#666";
    creditDiv.textContent =
      "Data sources: Land Cover Classification, Global Forest Watch, Hansen Global Forest Change, Fire Forest Loss Collection, Biomass Data 2020, Soil Data";
    contentDiv.appendChild(creditDiv);
  };

  const addGreenZoneVisualizationToMap = (polygon, data) => {
    const mapInstance = window.layersMapInstance || map;
    if (!mapInstance) return;

    const greenZoneId = "green-zone-analysis-layer";

    try {
      if (!mapInstance.getSource(greenZoneId)) {
        mapInstance.addSource(greenZoneId, {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: polygon.geometry,
            properties: {
              greenPercentage: data.greenZonePercentage,
            },
          },
        });
      } else {
        mapInstance.getSource(greenZoneId).setData({
          type: "Feature",
          geometry: polygon.geometry,
          properties: {
            greenPercentage: data.greenZonePercentage,
          },
        });
      }

      if (!mapInstance.getLayer(greenZoneId)) {
        mapInstance.addLayer({
          id: greenZoneId,
          type: "fill",
          source: greenZoneId,
          paint: {
            "fill-color": [
              "interpolate",
              ["linear"],
              ["get", "greenPercentage"],
              0,
              "#ff6b6b",
              50,
              "#ffdd57",
              100,
              "#23d160",
            ],
            "fill-opacity": 0.5,
            "fill-outline-color": "#000",
          },
        });

        mapInstance.addLayer({
          id: `${greenZoneId}-outline`,
          type: "line",
          source: greenZoneId,
          paint: {
            "line-color": "#000",
            "line-width": 2,
          },
        });
      }
    } catch (err) {
      console.error("Error adding green zone visualization to map:", err);
    }
  };

  const addForestCoverVisualizationToMap = (polygon, data) => {
    const mapInstance = window.layersMapInstance || map;
    if (!mapInstance) return;

    const forestCoverId = "forest-cover-analysis-layer";

    try {
      const offsetPolygon = { ...polygon, geometry: { ...polygon.geometry } };

      if (!mapInstance.getSource(forestCoverId)) {
        mapInstance.addSource(forestCoverId, {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: offsetPolygon.geometry,
            properties: {
              forestPercentage: data.forestCoverPercentage,
            },
          },
        });
      } else {
        mapInstance.getSource(forestCoverId).setData({
          type: "Feature",
          geometry: offsetPolygon.geometry,
          properties: {
            forestPercentage: data.forestCoverPercentage,
          },
        });
      }

      if (!mapInstance.getLayer(forestCoverId)) {
        mapInstance.addLayer({
          id: forestCoverId,
          type: "fill",
          source: forestCoverId,
          paint: {
            "fill-color": [
              "interpolate",
              ["linear"],
              ["get", "forestPercentage"],
              0,
              "#e65100",
              50,
              "#ff8f00",
              100,
              "#1b5e20",
            ],
            "fill-opacity": 0,
            "fill-outline-color": "#000",
          },
        });

        mapInstance.addLayer({
          id: `${forestCoverId}-outline`,
          type: "line",
          source: forestCoverId,
          paint: {
            "line-color": "#000",
            "line-width": 0,
          },
        });
      }
    } catch (err) {
      console.error("Error adding forest cover visualization to map:", err);
    }
  };

  const addVisualizationToggle = () => {
    const toggle = document.createElement("div");
    toggle.id = "visualization-toggle";
    Object.assign(toggle.style, {
      position: "fixed",
      top: "20px",
      left: "50%",
      transform: "translateX(-50%)",
      display: "flex",
      backgroundColor: "white",
      borderRadius: "4px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
      zIndex: 999,
    });

    const greenButton = document.createElement("button");
    greenButton.innerText = "Green Zone (A)";
    Object.assign(greenButton.style, {
      padding: "8px 12px",
      border: "none",
      backgroundColor: "#23d160",
      color: "white",
      fontWeight: "bold",
      borderRadius: "4px 0 0 4px",
      cursor: "pointer",
    });

    const forestButton = document.createElement("button");
    forestButton.innerText = "Forest Cover (B)";
    Object.assign(forestButton.style, {
      padding: "8px 12px",
      border: "none",
      backgroundColor: "#666",
      color: "white",
      fontWeight: "bold",
      borderRadius: "0 4px 4px 0",
      cursor: "pointer",
    });

    toggle.appendChild(greenButton);
    toggle.appendChild(forestButton);
    document.body.appendChild(toggle);

    const mapInstance = window.layersMapInstance || map;

    greenButton.addEventListener("click", () => {
      if (!mapInstance) return;
      try {
        greenButton.style.backgroundColor = "#23d160";
        forestButton.style.backgroundColor = "#666";
        mapInstance.setPaintProperty("green-zone-analysis-layer", "fill-opacity", 0.5);
        mapInstance.setPaintProperty("green-zone-analysis-layer-outline", "line-width", 2);
        mapInstance.setPaintProperty("forest-cover-analysis-layer", "fill-opacity", 0);
        mapInstance.setPaintProperty("forest-cover-analysis-layer-outline", "line-width", 0);
      } catch (err) {
        console.error("Error toggling visualization:", err);
      }
    });

    forestButton.addEventListener("click", () => {
      if (!mapInstance) return;
      try {
        greenButton.style.backgroundColor = "#666";
        forestButton.style.backgroundColor = "#1b5e20";
        mapInstance.setPaintProperty("green-zone-analysis-layer", "fill-opacity", 0);
        mapInstance.setPaintProperty("green-zone-analysis-layer-outline", "line-width", 0);
        mapInstance.setPaintProperty("forest-cover-analysis-layer", "fill-opacity", 0.5);
        mapInstance.setPaintProperty("forest-cover-analysis-layer-outline", "line-width", 2);
      } catch (err) {
        console.error("Error toggling visualization:", err);
      }
    });

    return toggle;
  };

  const removeAllVisualizationsFromMap = () => {
    const mapInstance = window.layersMapInstance || map;
    if (!mapInstance) {
      console.warn("Map instance not available for cleanup.");
      return;
    }

    const toggle = document.getElementById("visualization-toggle");
    if (toggle) toggle.remove();

    const greenZoneId = "green-zone-analysis-layer";
    const forestCoverId = "forest-cover-analysis-layer";

    try {
      if (mapInstance && typeof mapInstance.getLayer === 'function') {
        if (mapInstance.getLayer(`${greenZoneId}-outline`)) {
          mapInstance.removeLayer(`${greenZoneId}-outline`);
        }
        if (mapInstance.getLayer(greenZoneId)) {
          mapInstance.removeLayer(greenZoneId);
        }
        if (mapInstance.getSource(greenZoneId)) {
          mapInstance.removeSource(greenZoneId);
        }
      }
    } catch (err) {
      console.error("Error removing green zone visualization:", err);
    }

    try {
      if (mapInstance && typeof mapInstance.getLayer === 'function') {
        if (mapInstance.getLayer(`${forestCoverId}-outline`)) {
          mapInstance.removeLayer(`${forestCoverId}-outline`);
        }
        if (mapInstance.getLayer(forestCoverId)) {
          mapInstance.removeLayer(forestCoverId);
        }
        if (mapInstance.getSource(forestCoverId)) {
          mapInstance.removeSource(forestCoverId);
        }
      }
    } catch (err) {
      console.error("Error removing forest cover visualization:", err);
    }
  };

  const performComprehensiveAnalysis = async () => {
    console.log(`Performing comprehensive analysis of area: ${polygonArea ? polygonArea.toFixed(2) : 'N/A'} ha`);
    setIsAnalyzing(true);
    showAnalysisBox();
    const draw = getDrawInstance();
    if (!draw) {
      updateAnalysisBoxWithError("Draw tool not found. Please reload the page and try again.");
      setIsAnalyzing(false);
      return;
    }
    try {
      const features = draw.getAll().features;
      if (!features || features.length === 0) {
        updateAnalysisBoxWithError("No polygon found. Please draw a polygon first.");
        setIsAnalyzing(false);
        return;
      }
      const polygon = features[0];
      console.log("Found polygon:", polygon.geometry);

      const [
        greenZoneResponse,
        forestCoverResponse,
        formulaDResponse,
        bgbResponse,
        socResponse,
      ] = await Promise.all([
        fetch("/api/calculate_green_zone", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            geometry: polygon.geometry,
            greenZoneClasses,
          }),
        }),
        fetch("/api/forest_cover_analysis", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            geometry: polygon.geometry,
            treeCoverDensity: 30,
            minHeight: 5,
          }),
        }),
        fetch("/api/calculate_formula_d", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ geometry: polygon.geometry }),
        }),
        fetch("/api/calculate_bgb", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            geometry: polygon.geometry,
            year: "2020",
          }),
        }),
        fetch("/api/calculate_soc", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ geometry: polygon.geometry }),
        }),
      ]);

      if (!greenZoneResponse.ok) {
        const errorText = await greenZoneResponse.text();
        throw new Error(`Green Zone API error: ${greenZoneResponse.status} - ${errorText}`);
      }
      if (!forestCoverResponse.ok) {
        const errorText = await forestCoverResponse.text();
        throw new Error(`Forest Cover API error: ${forestCoverResponse.status} - ${errorText}`);
      }

      let formulaDData = null;
      if (formulaDResponse.ok) {
        formulaDData = await formulaDResponse.json();
        console.log("Formula D API response data:", formulaDData);
      } else {
        console.warn(`Formula D API error: ${formulaDResponse.status}`);
      }

      let bgbData = null;
      if (bgbResponse.ok) {
        bgbData = await bgbResponse.json();
        console.log("BGB API response data:", bgbData);
      } else {
        console.warn(`BGB API error: ${bgbResponse.status}`);
      }

      let socData = null;
      if (socResponse.ok) {
        socData = await socResponse.json();
        console.log("SOC API response data:", socData);
      } else {
        console.warn(`SOC API error: ${socResponse.status}`);
      }

      const greenZoneData = await greenZoneResponse.json();
      const forestCoverData = await forestCoverResponse.json();
      console.log("Green Zone API response data:", greenZoneData);
      console.log("Forest Cover API response data:", forestCoverData);

      setAnalysisResults({
        greenZone: greenZoneData,
        forestCover: forestCoverData,
        formulaD: formulaDData,
        bgb: bgbData,
        soc: socData,
      });

      updateAnalysisBoxWithResults(
        greenZoneData,
        forestCoverData,
        formulaDData,
        bgbData,
        socData
      );
    } catch (err) {
      console.error("Comprehensive analysis error:", err);
      updateAnalysisBoxWithError(err.message || "Error analyzing polygon.");
    } finally {
      setIsAnalyzing(false);
    }
  };


  useEffect(() => {
    setCalculatedAnnualCredits(null);
  }, [polygonArea]);

  useEffect(() => {
    console.log("Annual credits values:", {
      annualCredits,
      calculatedAnnualCredits,
      polygonArea,
      finalEligibleCO2: analysisResults.finalEligibleCO2,
    });
  }, [annualCredits, calculatedAnnualCredits, polygonArea, analysisResults.finalEligibleCO2]);

  useEffect(() => {
    return () => {
      const boxes = [
        document.getElementById("comprehensive-analysis-box"),
        document.getElementById("direct-analysis-box"),
        document.getElementById("forest-cover-analysis-box"),
      ];
      boxes.forEach((box) => {
        if (box) box.remove();
      });
      removeAllVisualizationsFromMap();
    };
  }, []);

  const handleDrawPolygon = () => {
    enterDrawingMode();
  };

  const handleClose = () => {
    hideDrawToolbar();
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  };

  const handleAnalyze = () => {
    console.log(`Comprehensive analysis initiated for area: ${polygonArea ? polygonArea.toFixed(2) : 'N/A'} ha`);
    performComprehensiveAnalysis();
  };

  const labelStyle = {
    display: "block",
    fontWeight: 600,
    marginBottom: "5px",
    color: "#2e3a59",
    fontSize: "14px",
  };

  const readonlyFieldStyle = {
    padding: "10px",
    background: "#f7f9fc",
    borderRadius: "6px",
    border: "1px solid #e1e5eb",
    color: "#2e3a59",
    fontWeight: 500,
  };

  const sectionStyle = {
    marginBottom: "16px",
  };

  const buttonStyle = {
    width: "100%",
    padding: "12px",
    marginBottom: "20px",
    backgroundColor: "#2e3a59",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "15px",
    fontFamily: "'Figtree', sans-serif",
    transition: "background-color 0.2s ease",
    boxShadow: "0 2px 4px rgba(46, 58, 89, 0.2)",
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <div
        style={{
          position: "absolute",
          top: "1%",
          right: "64px",
          width: "360px",
          padding: "24px",
          background: "white",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 1000,
          fontFamily: "'Figtree', sans-serif",
          borderRadius: "12px",
          color: "#2e3a59",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "20px",
              fontWeight: 700,
              color: "#2e3a59",
            }}
          >
            Land Analysis
          </h3>
          <button
            onClick={handleClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "20px",
              fontWeight: 700,
            }}
            aria-label="Close panel"
          >
            ×
          </button>
        </div>

        <div style={sectionStyle}>
          <label style={labelStyle}>Land Size (ha):</label>
          <div style={readonlyFieldStyle}>
            {typeof polygonArea === 'number' ? polygonArea.toFixed(2) : '0.00'}
          </div>
        </div>

        <button onClick={handleDrawPolygon} style={buttonStyle}>
          Draw Polygon
        </button>

        <div style={sectionStyle}>
          <label style={labelStyle}>Discount rate:</label>
          <div style={readonlyFieldStyle}>{discountRate.toFixed(2)}</div>
        </div>
        <div style={sectionStyle}>
          <label style={labelStyle}>Establishment cost per ha:</label>
          <div style={readonlyFieldStyle}>{estCost.toFixed(2)}</div>
        </div>
        <div style={sectionStyle}>
          <label style={labelStyle}>Maintenance cost per ha:</label>
          <div style={readonlyFieldStyle}>{maintenanceCost.toFixed(2)}</div>
        </div>
        <div style={sectionStyle}>
          <label style={labelStyle}>Starting carbon price ($/tCO2):</label>
          <div style={readonlyFieldStyle}>{carbonPrice.toFixed(2)}</div>
        </div>
        <div style={sectionStyle}>
          <label style={labelStyle}>Growth rate after Year 5:</label>
          <div style={readonlyFieldStyle}>{growthRate.toFixed(2)}</div>
        </div>
        <div style={sectionStyle}>
          <label style={labelStyle}>Timeline (years):</label>
          <div style={readonlyFieldStyle}>{timeline}</div>
        </div>
        <div style={sectionStyle}>
  <label style={labelStyle}>Annual Credits (tCO2):</label>
<div
  style={{
    ...readonlyFieldStyle,
    fontWeight: calculatedAnnualCredits ? 700 : 500,
    color: calculatedAnnualCredits ? '#4527a0' : '#2e3a59',
    background: calculatedAnnualCredits ? '#ede7f6' : '#f7f9fc',
  }}
>
  {calculatedAnnualCredits
    ? calculatedAnnualCredits.toFixed(2)
    : annualCredits
      ? annualCredits.toFixed(2)
      : "Pending analysis..."}
</div>
  {calculatedAnnualCredits && (
    <div style={{ fontSize: '12px', color: '#4527a0', marginTop: '4px', paddingLeft: '4px' }}>
      Updated from analysis results
    </div>
  )}
</div>

        {typeof polygonArea === 'number' && polygonArea > 0 && (
          <button
            onClick={handleAnalyze}
            style={{
              ...buttonStyle,
              backgroundColor: isAnalyzing ? "#666" : "#2e7d32",
            }}
            disabled={isAnalyzing}
          >
            {isAnalyzing ? "Analyzing..." : "Analyze Land Cover"}
          </button>
        )}

        {typeof polygonArea === 'number' && polygonArea > 0 && analysisResults.greenZone && analysisResults.forestCover && (
          <div
            style={{
              marginTop: "10px",
              padding: "10px",
              backgroundColor: "#f5f5f5",
              borderRadius: "6px",
              fontSize: "14px",
            }}
          >
            <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
              Analysis Results:
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Green Zone (A):</span>
              <span style={{ fontWeight: "600" }}>
                {analysisResults.greenZone.greenZonePercentage.toFixed(2)}%
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "4px",
              }}
            >
              <span>Forest Cover (B):</span>
              <span style={{ fontWeight: "600" }}>
                {analysisResults.forestCover.forestCoverPercentage.toFixed(2)}%
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "4px",
                padding: "4px 0",
                borderTop: "1px solid #e0e0e0",
                color: "#4527a0",
              }}
            >
              <span>Formula E (A×B):</span>
              <span style={{ fontWeight: "700" }}>
                {(
                  (analysisResults.greenZone.greenZonePercentage / 100) *
                  (analysisResults.forestCover.forestCoverPercentage / 100) *
                  100
                ).toFixed(2)}%
              </span>
            </div>
            {analysisResults.formulaD && (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "4px",
                    padding: "4px 0",
                    borderTop: "1px solid #e0e0e0",
                    color: "#2e3a59",
                  }}
                >
                  <span>Formula D:</span>
                  <span style={{ fontWeight: "700" }}>
                    {analysisResults.formulaD.formulaD.toFixed(2)}%
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "4px",
                    padding: "4px 0",
                    borderTop: "1px solid #e0e0e0",
                    color: "#c2185b",
                  }}
                >
                  <span>Formula F (30y):</span>
                  <span style={{ fontWeight: "700" }}>
                    {(
                      (1 - Math.pow(1 - analysisResults.formulaD.formulaD / 100, 30)) * 100
                    ).toFixed(2)}%
                  </span>
                </div>
              </>
            )}
            {analysisResults.bgb && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "10px",
                  backgroundColor: "#e8f5e9",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              >
                <div
                  style={{
                    fontWeight: "bold",
                    marginBottom: "8px",
                    color: "#1b5e20",
                  }}
                >
                  Biomass Results:
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Above-Ground (AGB):</span>
                  <span style={{ fontWeight: "600" }}>
                    {typeof analysisResults.bgb.agbPerHa === 'number'
                      ? analysisResults.bgb.agbPerHa.toFixed(2)
                      : "N/A"} t/ha
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "4px",
                  }}
                >
                  <span>Below-Ground (BGB):</span>
                  <span style={{ fontWeight: "600" }}>
                    {typeof analysisResults.bgb.bgbPerHa === 'number'
                      ? analysisResults.bgb.bgbPerHa.toFixed(2)
                      : "N/A"} t/ha
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "4px",
                    padding: "4px 0",
                    borderTop: "1px solid #c8e6c9",
                    color: "#33691e",
                  }}
                >
                  <span>Below-Ground Carbon:</span>
                  <span style={{ fontWeight: "700" }}>
                    {typeof analysisResults.bgb.bgbPerHa === 'number'
                      ? (analysisResults.bgb.bgbPerHa * 0.475 * 3.67).toFixed(2)
                      : "N/A"} tCO₂e/ha
                  </span>
                </div>
                <div style={{ fontSize: "11px", color: "#558b2f", marginBottom: "4px" }}>
                  BGC1 = (BGB * 0.475) * 3.67
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "4px",
                    padding: "4px 0",
                    borderTop: "1px dashed #c8e6c9",
                    color: "#33691e",
                  }}
                >
                  <span>BGC with 10-yr Decay:</span>
                  <span style={{ fontWeight: "700" }}>
                    {typeof analysisResults.bgb.bgbPerHa === 'number'
                      ? ((analysisResults.bgb.bgbPerHa * 0.475 * 3.67) * 0.744).toFixed(2)
                      : "N/A"} tCO₂e/ha
                  </span>
                </div>
                <div style={{ fontSize: "11px", color: "#558b2f", marginBottom: "4px" }}>
                  BGC2 = BGC1 * 0.744
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent:"space-between",
                    marginTop: "4px",
                    paddingTop: "4px",
                    borderTop: "1px solid #c8e6c9",
                  }}
                >
                  <span>Total Biomass:</span>
                  <span style={{ fontWeight: "700" }}>
                    {typeof analysisResults.bgb.agbPerHa === 'number' && typeof analysisResults.bgb.bgbPerHa === 'number'
                      ? (analysisResults.bgb.agbPerHa + analysisResults.bgb.bgbPerHa).toFixed(2)
                      : "N/A"} t/ha
                  </span>
                </div>
              </div>
            )}
            {analysisResults.soc && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "10px",
                  backgroundColor: "#e1f5fe",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              >
                <div
                  style={{
                    fontWeight: "bold",
                    marginBottom: "8px",
                    color: "#01579b",
                  }}
                >
                  Soil Organic Carbon:
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>SOC1 (0-30cm):</span>
                  <span style={{ fontWeight: "600" }}>
                    {analysisResults.soc.soc1PerHa !== undefined
                      ? parseFloat(analysisResults.soc.soc1PerHa).toFixed(2)
                      : "N/A"} t/ha
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "4px",
                  }}
                >
                  <span>SOC2 (30-100cm):</span>
                  <span style={{ fontWeight: "600" }}>
                    {analysisResults.soc.soc2PerHa !== undefined
                      ? parseFloat(analysisResults.soc.soc2PerHa).toFixed(2)
                      : "N/A"} t/ha
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "4px",
                    paddingTop: "4px",
                    borderTop: "1px solid #b3e5fc",
                  }}
                >
                  <span>SOC CO₂e:</span>
                  <span style={{ fontWeight: "700" }}>
                    {analysisResults.soc.socCO2ePerHa !== undefined
                      ? parseFloat(analysisResults.soc.socCO2ePerHa).toFixed(2)
                      : "N/A"} tCO₂e/ha
                  </span>
                </div>
              </div>
            )}
            {analysisResults.finalEligibleCO2 && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "10px",
                  backgroundColor: "#ede7f6",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              >
                <div
                  style={{
                    fontWeight: "bold",
                    marginBottom: "8px",
                    color: "#4527a0",
                  }}
                >
                  Carbon Credit Eligibility:
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Eligible CO₂:</span>
                  <span style={{ fontWeight: "600" }}>
                    {analysisResults.eligibleCO2} tCO₂e/ha
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "4px",
                  }}
                >
                  <span>Final Eligible CO₂ (20% buffer):</span>
                  <span style={{ fontWeight: "700" }}>
                    {analysisResults.finalEligibleCO2} tCO₂e/ha
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "4px",
                    paddingTop: "4px",
                    borderTop: "1px dashed #d1c4e9",
                  }}
                >
                  <span>Annual Credits CO₂:</span>
                  <span style={{ fontWeight: "700" }}>
                    {(parseFloat(analysisResults.finalEligibleCO2) / timeline).toFixed(2)} tCO₂e/ha/year
                  </span>
                </div>
                {typeof polygonArea === 'number' && polygonArea > 0 && analysisResults.finalEligibleCO2 !== "N/A" && (
                  <>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: "8px",
                        paddingTop: "8px",
                        borderTop: "1px solid #d1c4e9",
                        fontWeight: "600",
                      }}
                    >
                      <span>Total Final Eligible CO₂:</span>
                      <span>
                        {(parseFloat(analysisResults.finalEligibleCO2) * polygonArea).toFixed(2)} tCO₂e
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: "4px",
                      }}
                    >
                      <span>Total Annual Credits:</span>
                      <span>
                        {(parseFloat(analysisResults.finalEligibleCO2) * polygonArea / timeline).toFixed(2)} tCO₂e/year
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}
            {financialResults && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "10px",
                  backgroundColor: "#e8f5e9",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              >
                <div
                  style={{
                    fontWeight: "bold",
                    marginBottom: "8px",
                    color: "#2e7d32",
                  }}
                >
                  30-Year Financial Summary:
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Total Revenue:</span>
                  <span style={{ fontWeight: "600" }}>
                    ${financialResults.totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "4px",
                  }}
                >
                  <span>Total Costs:</span>
                  <span style={{ fontWeight: "600" }}>
                    ${financialResults.totalCosts.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "4px",
                    paddingTop: "4px",
                    borderTop: "1px solid #c8e6c9",
                  }}
                >
                  <span>Net Present Value (NPV):</span>
                  <span style={{ fontWeight: "700", color: "#2e7d32" }}>
                    ${financialResults.npv.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
                <div style={{ fontSize: "12px", color: "#558b2f", marginTop: "8px", textAlign: "center" }}>
                  Based on {(calculatedAnnualCredits || annualCredits).toLocaleString(undefined, { maximumFractionDigits: 0 })} annual credits at ${carbonPrice.toFixed(2)}/tCO₂e initial price, growing {(growthRate * 100).toFixed(0)}% annually after year 5
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {isVisible && <CustomDrawButtons />}
    </>
  );
};

CarbonAnalysisPanel.propTypes = {
  map: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  onAnalyzePolygon: PropTypes.func,
};

export default CarbonAnalysisPanel;
